/**
 * Planner state <-> URL query. Waypoints are packed as `lon,lat` pairs joined
 * by `;` so a shared link stays readable and short enough to paste anywhere.
 */

import { RouteOptions, RouteProfile, Waypoint } from '../types';
import { DEFAULT_OPTIONS, makeWaypointId } from '../plannerReducer';

const PARAM = {
  waypoints: 'rp',
  profile: 'rpp',
  exclude: 'rpx',
  departAt: 'rpd',
  units: 'rpu',
};

const PROFILES: RouteProfile[] = ['driving-traffic', 'driving', 'walking', 'cycling'];

export function encodePlannerParams(waypoints: Waypoint[], options: RouteOptions): URLSearchParams {
  const params = new URLSearchParams();
  if (waypoints.length > 0) {
    params.set(
      PARAM.waypoints,
      waypoints.map((w) => `${w.longitude.toFixed(5)},${w.latitude.toFixed(5)}`).join(';'),
    );
  }
  if (options.profile !== DEFAULT_OPTIONS.profile) params.set(PARAM.profile, options.profile);
  const excludes = Object.entries(options.exclude).filter(([, on]) => on).map(([k]) => k);
  if (excludes.length) params.set(PARAM.exclude, excludes.join(','));
  if (options.departAt) params.set(PARAM.departAt, options.departAt);
  if (options.units !== DEFAULT_OPTIONS.units) params.set(PARAM.units, options.units);
  return params;
}

export function buildPermalink(waypoints: Waypoint[], options: RouteOptions, base?: string): string {
  const url = new URL(base || window.location.href);
  // Drop any stale planner params before writing the current ones.
  Object.values(PARAM).forEach((key) => url.searchParams.delete(key));
  encodePlannerParams(waypoints, options).forEach((value, key) => url.searchParams.set(key, value));
  return url.toString();
}

export interface DecodedPlannerState {
  waypoints: Waypoint[];
  options: Partial<RouteOptions>;
}

export function decodePlannerParams(search: string | URLSearchParams): DecodedPlannerState | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const raw = params.get(PARAM.waypoints);
  if (!raw) return null;

  const waypoints: Waypoint[] = raw
    .split(';')
    .map((pair) => pair.split(','))
    .flatMap(([lon, lat]) => {
      const longitude = Number(lon);
      const latitude = Number(lat);
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return [];
      if (Math.abs(longitude) > 180 || Math.abs(latitude) > 90) return [];
      return [{
        id: makeWaypointId(),
        longitude,
        latitude,
        labelStatus: 'idle' as const,
        source: 'import' as const,
      }];
    });

  if (waypoints.length === 0) return null;

  const options: Partial<RouteOptions> = {};
  const profile = params.get(PARAM.profile) as RouteProfile | null;
  if (profile && PROFILES.includes(profile)) options.profile = profile;

  const excludeRaw = params.get(PARAM.exclude);
  if (excludeRaw) {
    const on = new Set(excludeRaw.split(','));
    options.exclude = {
      toll: on.has('toll'),
      motorway: on.has('motorway'),
      ferry: on.has('ferry'),
      unpaved: on.has('unpaved'),
    };
  }

  const departAt = params.get(PARAM.departAt);
  if (departAt) options.departAt = departAt;

  const units = params.get(PARAM.units);
  if (units === 'km' || units === 'mi') options.units = units;

  return { waypoints, options };
}
